import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, Input, ChangeDetectorRef, EventEmitter, Output } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ContractCancel, OnContractExpiry, PlanCategory } from '@fitness-force/enum';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { PopoverModule } from 'primeng/popover';
import { StyleClassModule } from 'primeng/styleclass';

import { ToastMessageService } from '@fitness-force/api';
import { CancelSubscriptionRequest } from '@fitness-force/models';
import { ClientSubscriptionService } from '@fitness-force/api';
import { RadioButtonModule } from 'primeng/radiobutton';
import { MessageModule } from 'primeng/message';


@Component({
  selector: 'subscription-cancel',
  templateUrl: './subscription-cancel.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ToastMessageService],
  standalone: true,
  imports: [CommonModule,
    FormsModule,
    ReactiveFormsModule,
    DialogModule,
    StyleClassModule,
    ButtonModule,
    PopoverModule,
    RadioButtonModule,
    MessageModule,
    TranslateModule]
})
export class SubscriptionCancelComponent implements OnInit {
  @Input('visible') visible = false;
  @Input('subscription') subscriptionDetails: any;
  @Input('clientId') clientId: number = 0;
  @Input('tenantId') tenantId: number = 0;
  @Output('closeEvent') closeEvent: EventEmitter<{reloadData: boolean, subscription:any}> =new EventEmitter();


  CancelSubscriptionForm: UntypedFormGroup = new UntypedFormGroup({});
  isFormSubmitting:boolean=false;
  CancelType: ContractCancel;
  cancellationDate:Date;
  text:string='TODAY';
  selectedCancelType:string='ImmediateCancel';

  constructor(private fb: UntypedFormBuilder, private cd: ChangeDetectorRef,
    private toastMessageService: ToastMessageService, public translate: TranslateService,
    private clientSubscriptionService : ClientSubscriptionService) { }

  ngOnInit(): void {
    this.cancellationDate= new Date();
    this.CancelSubscriptionForm = this.fb.group({
      cancel_type: ['ImmediateCancel', Validators.required],
      reason: [''],
    });
  }



  GetCancelType(cancelType: string) {
    let cancelTypeEnum=  ContractCancel[cancelType as keyof typeof ContractCancel]
    this.CancelTypeFormControl.setValue(cancelType);
    this.selectedCancelType = cancelType;
    if(cancelTypeEnum == ContractCancel.ImmediateCancel || cancelTypeEnum.toString() == ContractCancel[ContractCancel.ImmediateCancel]) {
      this.cancellationDate= new Date();
      this.text='TODAY';
    } 
    else if(cancelType ==ContractCancel[ContractCancel.EndOfBillingPeriod]){
      this.text='END_OF_BILLING';
      const minusOneDay = new Date(this.subscriptionDetails.next_charge_date);
      minusOneDay.setDate(new Date(this.subscriptionDetails.next_charge_date).getDate() - 1);
      this.cancellationDate= minusOneDay;
    }
    else if(cancelType == ContractCancel[ContractCancel.EndOfContract]) {
      this.cancellationDate= this.subscriptionDetails.end_date;
      this.text="LAST_DAY"
    }
  }

  get ShowEndOfBillingButton() {
    const category = this.subscriptionDetails.subscription_category;
    if (category == PlanCategory.PaidInFull || category == PlanCategory[PlanCategory.PaidInFull]) return false;
    // else if (category == PlanCategory.PayAsYouGo || category == PlanCategory[PlanCategory.PayAsYouGo]) return true;
    // else if (category == PlanCategory.Contract || category == PlanCategory[PlanCategory.Contract]) return true;
    else return true;
  }

  get ShowEndOfContractButton() {
    const category = this.subscriptionDetails.subscription_category;
    const contractExpiry = this.subscriptionDetails.on_contract_expiry;
    if (category == PlanCategory.PaidInFull || category == PlanCategory[PlanCategory.PaidInFull]) {
      if (contractExpiry == OnContractExpiry.ContractAutoRenew
        || contractExpiry == OnContractExpiry[OnContractExpiry.ContractAutoRenew]
        || (contractExpiry == OnContractExpiry.ContractConvertsToPayAsYouGo
        || contractExpiry == OnContractExpiry[OnContractExpiry.ContractConvertsToPayAsYouGo])) {
        return true;
      }
      else
        return false;
    }
    else if (category == PlanCategory.PayAsYouGo || category == PlanCategory[PlanCategory.PayAsYouGo]) return false;
    else if (category == PlanCategory.Contract || category == PlanCategory[PlanCategory.Contract])
    {
      if (contractExpiry == OnContractExpiry.ContractAutoRenew
        || contractExpiry == OnContractExpiry[OnContractExpiry.ContractAutoRenew]
        || (contractExpiry == OnContractExpiry.ContractConvertsToPayAsYouGo
        || contractExpiry == OnContractExpiry[OnContractExpiry.ContractConvertsToPayAsYouGo])) {
        return true;
      }
     else return false;
    }
    else return false;
  }

  CancelSubscription()
  {
    if (this.CancelSubscriptionForm.valid) {
      let cancelSubscription: CancelSubscriptionRequest = {} as CancelSubscriptionRequest;
      cancelSubscription.cancellation_reason = this.CancelSubscriptionForm.value.reason;
      cancelSubscription.subscription_cancel= this.CancelTypeFormControl.value;
      this.isFormSubmitting = true;
      this.clientSubscriptionService.CancelSubscription(this.tenantId,this.clientId, this.subscriptionDetails.id, cancelSubscription).subscribe({
        next: async (res: any) => {
          this.isFormSubmitting = false;
          this.translate.get('SUBSCRIPTION_CANCEL.MSG_SUCCESS')
            .subscribe((translation) => {
              this.toastMessageService.showSuccess(translation);
            });
            await this.timeout(1000);
            this.subscriptionDetails.scheduled_cancellation_date = this.cancellationDate.toUTCString();
            this.subscriptionDetails.cancel_reason = this.CancelTypeFormControl.value;
            this.closePopup(true);
        },
        error: (error: any) => {
          this.isFormSubmitting = false;
          this.handleError(error);
        },
      });
    } else {
      // Todo Fire all control touches ...
      Object.keys(this.CancelSubscriptionForm.controls).forEach((field) => {
        const control = this.CancelSubscriptionForm.get(field);
        control?.markAsTouched({ onlySelf: true });
      });
      this.cd.detectChanges();
    }
  }

  handleError(error: any) {
    if (error.originalError.error.length > 0) {
      error.originalError.error.forEach((e: any) => {
          this.toastMessageService.showErrorMessage(e);
      });
    } else {
      this.toastMessageService.showErrorMessage(error);
    }
  }

  closePopup(reloadData:boolean)
  {
    this.visible = false;
    this.CancelSubscriptionForm.reset();
    this.closeEvent.emit({reloadData:reloadData, subscription:this.subscriptionDetails});
  }

  timeout(ms: any) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  isMinimumDateTimeValue(eta: Date): Boolean {
    return new Date(eta).getTime() === new Date('0001-01-01T00:00:00').getTime();
  }

  get CancelTypeFormControl(): UntypedFormControl {
    return this.CancelSubscriptionForm.controls.cancel_type as UntypedFormControl;
  }

  get isVisible(): boolean {
    return this.visible && this.subscriptionDetails;
  }

}
